import { useState, useEffect, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabaseClient } from '@/configs/supabase'
import {
  getCustomerById,
  getDefaultCustomerPricing,
} from '@/supabase/Customer/customer'
import { PricingData } from '@/utils/priceCalculations'
import {
  ICustomerPricing,
  ICustomerWithPricing,
} from '@/supabase/Customer/customer.interface'

export interface UsePricingDataReturn {
  pricingData: PricingData | null
  isLoading: boolean
  error: string | null
}

// Simple cache configuration
const getCacheConfig = (type: 'customer' | 'default') => {
  return {
    staleTime: type === 'customer' ? 30000 : 60000, // 30s for customer, 60s for default
    cacheTime: type === 'customer' ? 300000 : 600000, // 5min for customer, 10min for default
  }
}

/**
 * Custom hook for managing pricing data based on customer selection
 * Handles fallback to default pricing when customer pricing is not available
 *
 * @param customerId - Customer ID to fetch pricing for (optional)
 * @returns Pricing data, loading state, and error state
 */
export function usePricingData(
  customerId?: string | null
): UsePricingDataReturn {
  const [error, setError] = useState<string | null>(null)

  // Query for customer-specific pricing
  const {
    data: customerData,
    isLoading: isLoadingCustomer,
    error: customerError,
  } = useQuery<ICustomerWithPricing | null>({
    queryKey: ['customer-pricing', customerId],
    queryFn: async () => {
      if (!customerId) return null

      try {
              const customer = await getCustomerById(customerId, supabaseClient)
        return customer
      } catch (err) {
        console.warn(`Failed to fetch customer ${customerId}:`, err)
        return null
      }
    },
    enabled: !!customerId,
    ...getCacheConfig('customer'),
    refetchOnWindowFocus: false,
  })

  // Query for default pricing (fallback)
  const {
    data: defaultPricing,
    isLoading: isLoadingDefault,
    error: defaultError,
  } = useQuery<ICustomerPricing | null>({
    queryKey: ['default-pricing'],
    queryFn: async () => {
      try {
        const pricing = await getDefaultCustomerPricing(supabaseClient)
        return pricing
      } catch (err) {
        console.error('Failed to fetch default pricing:', err)
        throw err
      }
    },
    ...getCacheConfig('default'),
    refetchOnWindowFocus: false,
  })

  // Transform pricing data to our interface format (memoized)
  const transformPricingData = useCallback(
    (pricing: ICustomerPricing): PricingData => {
      return {
        largeIceCubePriceTHB: pricing.large_ice_cube_price_thb,
        smallIceCubePriceTHB: pricing.small_ice_cube_price_thb,
        crushedIcePriceTHB: pricing.crushed_ice_price_thb,
      }
    },
    []
  )

  // Validate transformed pricing data (memoized)
  const validateTransformedPricing = useCallback(
    (pricing: PricingData): boolean => {
      return (
        pricing &&
        typeof pricing.largeIceCubePriceTHB === 'number' &&
        typeof pricing.smallIceCubePriceTHB === 'number' &&
        typeof pricing.crushedIcePriceTHB === 'number' &&
        isFinite(pricing.largeIceCubePriceTHB) &&
        isFinite(pricing.smallIceCubePriceTHB) &&
        isFinite(pricing.crushedIcePriceTHB) &&
        pricing.largeIceCubePriceTHB >= 0 &&
        pricing.smallIceCubePriceTHB >= 0 &&
        pricing.crushedIcePriceTHB >= 0
      )
    },
    []
  )

  // Determine which pricing to use and transform it
  const pricingData = useMemo((): PricingData | null => {
    try {
      // Clear any previous errors when recalculating
      setError(null)

      console.log('🧮 usePricingData - Processing pricing for customer:', customerId)
      console.log('🧮 usePricingData - customerData:', customerData)
      console.log('🧮 usePricingData - defaultPricing:', defaultPricing)

      // Case 1: Customer has pricing - use customer pricing
      if (customerId && customerData?.pricing) {
        console.log('✅ Using customer-specific pricing:', customerData.pricing)
        const transformed = transformPricingData(customerData.pricing)
        // Validate transformed data
        if (validateTransformedPricing(transformed)) {
          console.log('✅ Customer pricing is valid, returning:', transformed)
          return transformed
        } else {
          console.warn('usePricingData: Invalid customer pricing data')
          // Fall through to default pricing
        }
      }

      // Case 2: No customer selected OR customer exists but no pricing OR customer not found
      // - use default pricing
      if (defaultPricing) {
        console.log('🔄 Using default pricing:', defaultPricing)
        const transformed = transformPricingData(defaultPricing)
        if (validateTransformedPricing(transformed)) {
          console.log('✅ Default pricing is valid, returning:', transformed)
          return transformed
        } else {
          console.error('usePricingData: Invalid default pricing data')
          setError(
            'Invalid default pricing data. Please contact system administrator'
          )
          return null
        }
      }

      // Case 3: No pricing available at all
      
      setError(
        'Pricing data not found. Please check default pricing configuration'
      )
      return null
    } catch (error) {
      console.error('usePricingData: Error processing pricing data:', error)
      setError('Error processing pricing data')
      return null
    }
  }, [
    customerId,
    customerData,
    defaultPricing,
    transformPricingData,
    validateTransformedPricing,
  ])

  // Determine loading state
  const isLoading = useMemo(() => {
    // If customer query is loading
    if (customerId && isLoadingCustomer) return true

    // If default pricing is loading (needed for fallback or when no customer selected)
    if (isLoadingDefault) return true

    return false
  }, [customerId, isLoadingCustomer, isLoadingDefault])

  // Handle errors
  useEffect(() => {
    if (customerError && !customerData) {
      console.warn('Customer query error:', customerError)
      // Don't set error here as we'll fallback to default pricing
    }

    if (defaultError) {
      console.error('Default pricing error:', defaultError)
      setError('Unable to load default pricing data')
    }
  }, [customerError, defaultError, customerData])

  return {
    pricingData,
    isLoading,
    error,
  }
}

/**
 * Hook specifically for getting default pricing data
 * Useful for components that always need default pricing
 */
export function useDefaultPricing(): UsePricingDataReturn {
  const {
    data: defaultPricing,
    isLoading,
    error: queryError,
  } = useQuery<ICustomerPricing | null>({
    queryKey: ['default-pricing'],
    queryFn: async () => {
      const pricing = await getDefaultCustomerPricing(supabaseClient)
      if (!pricing) {
        throw new Error('Default pricing not found')
      }
      return pricing
    },
    ...getCacheConfig('default'),
    refetchOnWindowFocus: false,
  })

  const pricingData = useMemo((): PricingData | null => {
    if (!defaultPricing) return null

    return {
      largeIceCubePriceTHB: defaultPricing.large_ice_cube_price_thb,
      smallIceCubePriceTHB: defaultPricing.small_ice_cube_price_thb,
      crushedIcePriceTHB: defaultPricing.crushed_ice_price_thb,
    }
  }, [defaultPricing])

  return {
    pricingData,
    isLoading,
    error: queryError ? 'Unable to load default pricing data' : null,
  }
}
